import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

/* V12 — "Nomad Craft"
   Tactile and artisanal. Earthy neutrals, rough paper textures,
   hand-drawn vector accents, serif + sans-serif pairing.
   Newsreader (editorial serif) + DM Sans (modern sans). */

const paper = '#f2ebe0';
const ink = '#2c2417';
const terracotta = '#b5634b';
const sage = '#7a8f6e';
const warmGrey = '#8a7e6f';

const features = [
  { marker: '✦', title: 'Agenda intelligent', desc: 'Optimisation des créneaux tissée dans votre rythme quotidien.' },
  { marker: '✧', title: 'Dossiers patients', desc: 'Chaque historique est une histoire, accessible d\'un geste.' },
  { marker: '✦', title: 'Facturation fluide', desc: 'Devis et factures cousus dans un flux artisanal continu.' },
  { marker: '✧', title: 'Multi-cabinet', desc: 'Un fil conducteur unique pour tous vos cabinets.' },
];

export default function LandingV12() {
  const serif = "'Newsreader', serif";
  const sans = "'DM Sans', sans-serif";

  /* SVG paper texture (crosshatch) */
  const paperTexture = `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0l60 60M60 0L0 60' stroke='%23c4b99a' stroke-width='0.3' fill='none' opacity='0.15'/%3E%3C/svg%3E")`;

  return (
    <div className="doctif-page" style={{ fontFamily: sans, background: paper, color: ink }}>
      <Helmet><title>doctif — Nomad Craft</title></Helmet>

      {/* Paper texture overlay */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none',
        backgroundImage: paperTexture, backgroundRepeat: 'repeat',
      }} />

      {/* ── Header ── */}
      <header className="doctif-shell" style={{ padding: '28px 0', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: serif, fontSize: 32, fontWeight: 400, fontStyle: 'italic', color: terracotta, letterSpacing: '-0.01em' }}>
            doctif
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <nav className="doctif-chip-row">
              {Array.from({ length: 15 }, (_, i) => (
                <a key={i} className="doctif-chip" href={`/${i + 1}/`} style={{
                  borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 500,
                  background: i === 11 ? terracotta : 'transparent', color: i === 11 ? paper : warmGrey,
                  border: `1px solid ${i === 11 ? terracotta : '#c4b99a'}`,
                  fontFamily: sans,
                }}>{String(i + 1).padStart(2, '0')}</a>
              ))}
            </nav>
            <a href="/accounts/login/" style={{
              fontFamily: sans, fontWeight: 600, fontSize: 13, color: ink,
              padding: '8px 18px', border: `1.5px solid ${ink}`, borderRadius: 4,
              textDecoration: 'none', transition: 'all 0.3s',
            }}>Connexion</a>
          </div>
        </div>
        {/* Hand-drawn line */}
        <svg aria-hidden height="6" style={{ width: '100%', marginTop: 16 }}>
          <path d="M0 3 Q 200 0, 400 3 T 800 3 T 1200 3" stroke={terracotta} strokeWidth="1.5" fill="none" opacity="0.4" />
        </svg>
      </header>

      {/* ── Hero ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 48, paddingBottom: 60 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 60, alignItems: 'center' }}>
          <div>
            <p className="doctif-fade-up" style={{
              fontFamily: sans, fontWeight: 500, fontSize: 12, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: sage, marginBottom: 20,
            }}>
              Fait à la main, pensé pour durer
            </p>
            <h1 className="doctif-fade-up doctif-delay-1" style={{
              fontFamily: serif, fontWeight: 400,
              fontSize: 'clamp(2.4rem, 5.5vw, 4.8rem)', lineHeight: 1.1,
              marginBottom: 24, letterSpacing: '-0.015em',
            }}>
              L'art du soin,<br />
              <em style={{ color: terracotta }}>façonné à la main.</em>
            </h1>
            <p className="doctif-fade-up doctif-delay-2" style={{
              fontSize: 16, lineHeight: 1.8, color: warmGrey, maxWidth: 440, marginBottom: 32,
            }}>
              doctif est un outil artisanal pour praticiens exigeants. Pas de plastique numérique —
              chaque interaction est pensée avec le soin d'un objet fait main.
            </p>
            <div className="doctif-fade-up doctif-delay-3" style={{ display: 'flex', gap: 14 }}>
              <a href="/accounts/login/" style={{
                display: 'inline-block', padding: '13px 30px', background: terracotta,
                color: paper, fontWeight: 600, fontSize: 14, borderRadius: 6,
                textDecoration: 'none', transition: 'all 0.3s',
              }}>Commencer</a>
              <a href="#modules" style={{
                display: 'inline-block', padding: '13px 30px',
                border: `1.5px solid ${ink}`, borderRadius: 6,
                color: ink, fontWeight: 600, fontSize: 14, textDecoration: 'none',
                transition: 'all 0.3s',
              }}>Découvrir</a>
            </div>
          </div>
          {/* Right: Artisanal card/notebook illustration */}
          <div className="doctif-fade-up doctif-delay-2" style={{
            background: '#ede5d8', borderRadius: 12, padding: 32,
            border: '1.5px solid #d4c9b5',
            boxShadow: '6px 6px 0 #d4c9b5',
            transform: 'rotate(1.5deg)',
          }}>
            <div style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 14, color: warmGrey, marginBottom: 16 }}>
              ~ carnet de bord ~
            </div>
            {/* Lined notebook entries */}
            {[
              { time: '09:00', label: 'Mme Dupont — Contrôle', status: '✓' },
              { time: '09:45', label: 'M. Bernard — Détartrage', status: '○' },
              { time: '10:30', label: 'Mme Leroy — Prothèse', status: '✓' },
              { time: '11:15', label: 'M. Petit — Consultation', status: '—' },
            ].map((e, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                borderBottom: `1px dashed #c4b99a`,
              }}>
                <span style={{ fontFamily: sans, fontSize: 12, color: sage, fontWeight: 600, minWidth: 44 }}>{e.time}</span>
                <span style={{ fontFamily: serif, fontSize: 14, color: ink, flex: 1 }}>{e.label}</span>
                <span style={{ fontFamily: serif, fontSize: 16, color: e.status === '✓' ? sage : warmGrey }}>{e.status}</span>
              </div>
            ))}
            <div style={{ marginTop: 16, fontFamily: serif, fontStyle: 'italic', fontSize: 12, color: '#b5a78a' }}>
              « Chaque jour est un projet à façonner »
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats — Earthy bar ── */}
      <div style={{ background: ink, padding: '20px 0', position: 'relative', zIndex: 5 }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', textAlign: 'center' }}>
          {[
            { v: '240+', l: 'artisans connectés' },
            { v: '61 000', l: 'rendez-vous / mois' },
            { v: '34 %', l: 'temps retrouvé' },
          ].map((s, i) => (
            <div key={i} style={{ borderRight: i < 2 ? `1px solid #483d2e` : 'none' }}>
              <div style={{ fontFamily: serif, fontSize: 30, color: paper, letterSpacing: '-0.02em' }}>{s.v}</div>
              <div style={{ fontSize: 11, color: '#8a7e6f', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features — Craft cards ── */}
      <section id="modules" className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 64, paddingBottom: 64 }}>
        <p style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 14, color: warmGrey, marginBottom: 8 }}>~ nos outils ~</p>
        <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', marginBottom: 40 }}>
          Quatre gestes essentiels
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
          {features.map((f, i) => (
            <div key={i} className="doctif-fade-up" style={{
              padding: '32px 28px', borderRadius: 10,
              background: '#f7f0e4', border: '1.5px solid #d4c9b5',
              boxShadow: `4px 4px 0 #d4c9b5`,
              animationDelay: `${i * 120}ms`, transition: 'transform 0.3s, box-shadow 0.3s',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 20, color: terracotta }}>{f.marker}</span>
                <h3 style={{ fontFamily: serif, fontWeight: 400, fontSize: 20, letterSpacing: '-0.01em' }}>{f.title}</h3>
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: warmGrey }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Workflow — Numbered steps with craft aesthetic ── */}
      <section style={{ background: '#ebe3d5', position: 'relative', zIndex: 5, padding: '60px 0' }}>
        <div className="doctif-shell">
          <p style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 14, color: warmGrey, marginBottom: 8 }}>~ le processus ~</p>
          <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: 'clamp(1.4rem, 2.5vw, 2.2rem)', marginBottom: 40 }}>
            En trois gestes
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 32 }}>
            {[
              { n: '01', title: 'Ouverture', desc: 'Créez votre espace en quelques minutes. Comme ouvrir un carnet neuf.' },
              { n: '02', title: 'Configuration', desc: 'Adaptez chaque module à votre pratique. Votre cabinet, vos règles.' },
              { n: '03', title: 'Pratique', desc: 'Lancez-vous. L\'outil s\'efface derrière votre expertise.' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  fontFamily: serif, fontSize: 48, color: terracotta, opacity: 0.3,
                  lineHeight: 1, marginBottom: 12,
                }}>{s.n}</div>
                <h3 style={{ fontFamily: serif, fontWeight: 400, fontSize: 18, marginBottom: 8 }}>{s.title}</h3>
                <p style={{ fontSize: 13, lineHeight: 1.7, color: warmGrey }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 60, paddingBottom: 60 }}>
        <p style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 14, color: warmGrey, marginBottom: 32 }}>~ ils racontent ~</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
          {[
            { q: 'Enfin un outil qui a l\'âme d\'un objet bien fait. Rien de superflu.', a: 'Dr. Elise Moreau', r: 'Paris' },
            { q: 'On sent le travail artisanal dans chaque détail de l\'interface.', a: 'Dr. Julien Fabre', r: 'Lyon' },
            { q: 'C\'est chaleureux et fonctionnel. Mes patients sentent la différence.', a: 'Dr. Nadia Hamdi', r: 'Marseille' },
          ].map((t, i) => (
            <div key={i} style={{
              padding: '24px', borderRadius: 8,
              background: '#f7f0e4', border: '1px dashed #c4b99a',
            }}>
              <p style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 15, lineHeight: 1.7, color: '#555', marginBottom: 16 }}>
                « {t.q} »
              </p>
              <p style={{ fontFamily: sans, fontWeight: 600, fontSize: 13, color: ink }}>{t.a}</p>
              <p style={{ fontSize: 12, color: warmGrey }}>{t.r}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust ── */}
      <div style={{ borderTop: '1.5px dashed #c4b99a', borderBottom: '1.5px dashed #c4b99a', position: 'relative', zIndex: 5 }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', padding: '18px 0', textAlign: 'center' }}>
          {[{ k: 'Chiffrement', v: 'AES-256' }, { k: 'Hébergement', v: 'HDS France' }, { k: 'Uptime', v: '99.9%' }, { k: 'Support', v: '< 2h' }].map((t, i) => (
            <div key={i}>
              <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: warmGrey, marginBottom: 2 }}>{t.k}</div>
              <div style={{ fontFamily: serif, fontSize: 16, color: ink }}>{t.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 72, paddingBottom: 72, textAlign: 'center' }}>
        <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: 'clamp(1.8rem, 4vw, 3.2rem)', marginBottom: 24, lineHeight: 1.15 }}>
          Prêt à façonner<br /><em style={{ color: terracotta }}>votre pratique ?</em>
        </h2>
        <a href="/accounts/login/" style={{
          display: 'inline-block', padding: '14px 40px', background: terracotta,
          color: paper, fontWeight: 600, fontSize: 15, borderRadius: 6,
          textDecoration: 'none', boxShadow: `3px 3px 0 #8e4630`, transition: 'all 0.3s',
        }}>Commencer l'aventure</a>
      </section>

      {/* ── Footer ── */}
      <footer style={{ position: 'relative', zIndex: 5, padding: '16px 0' }}>
        <svg aria-hidden height="6" style={{ width: '100%' }}>
          <path d="M0 3 Q 200 0, 400 3 T 800 3 T 1200 3" stroke={terracotta} strokeWidth="1" fill="none" opacity="0.3" />
        </svg>
        <div className="doctif-shell" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12 }}>
          <span style={{ fontFamily: serif, fontStyle: 'italic', fontSize: 13, color: warmGrey }}>doctif — Direction Nomad Craft</span>
          <span style={{ fontSize: 12, color: '#b5a78a' }}>v12.0 — {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
